import { useCallback, useEffect, useRef, useState } from "react";
import { format } from "date-fns";
import { Archive, FileText, Loader2, Upload } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";

const CATEGORY_LABELS = {
  referral_document: "Referral document",
  child_document: "Case document",
  consent_document: "Consent document",
  child_photo: "Photograph",
};

export default function DocumentsTab({ child }) {
  const { hasPermission } = useAuth();
  const isHistoric = child.record_type === "historic";
  const canUpload = hasPermission("referrals:manage") || hasPermission("children:update") || (isHistoric && hasPermission("children:historic"));
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState(isHistoric ? "child_document" : "referral_document");
  const [description, setDescription] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/files", { params: { child_id: child.id } });
      setItems(data.items);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [child.id]);

  useEffect(() => {
    load();
  }, [load]);

  const upload = async () => {
    const file = fileRef.current?.files?.[0];
    if (!file) {
      toast.error("Choose a file first");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("category", category);
      fd.append("child_id", child.id);
      if (child.latest_referral && category === "referral_document") fd.append("referral_id", child.latest_referral.id);
      if (description) fd.append("description", description);
      await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document uploaded securely");
      setDescription("");
      if (fileRef.current) fileRef.current.value = "";
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
    }
  };

  const archive = async (docId) => {
    try {
      await api.post(`/files/${docId}/archive`);
      toast.success("Document archived (retained, not deleted)");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const fmt = (v) => {
    try {
      return format(new Date(v), "dd MMM yyyy HH:mm");
    } catch {
      return "";
    }
  };

  return (
    <div className="space-y-4" data-testid="documents-tab">
      <p className="text-xs text-slate-500">
        Documents are stored privately and are never publicly accessible. Access is checked against case permissions
        on every download. Documents cannot be deleted — they can be archived by authorised administrators.
      </p>

      {canUpload && (
        <Card>
          <CardContent className="p-4 flex flex-col sm:flex-row gap-3 sm:items-end">
            <div className="space-y-1.5">
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger data-testid="doc-category-select" className="w-full sm:w-56"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="referral_document" data-testid="doc-cat-referral">Referral document</SelectItem>
                  <SelectItem value="child_document" data-testid="doc-cat-case">Case document</SelectItem>
                  <SelectItem value="consent_document" data-testid="doc-cat-consent">Consent document</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Input placeholder="Description (optional)" value={description} onChange={(e) => setDescription(e.target.value)}
              data-testid="doc-description-input" className="flex-1" />
            <Input type="file" accept="image/*,application/pdf" ref={fileRef} data-testid="doc-file-input" className="sm:w-64" />
            <Button onClick={upload} disabled={uploading} data-testid="doc-upload-button"
              className="h-10 bg-primary hover:bg-brand-hover text-white font-semibold">
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Upload className="h-4 w-4 mr-2" /> Upload</>}
            </Button>
          </CardContent>
        </Card>
      )}

      {loading ? (
        <div className="flex items-center gap-2 text-slate-600"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
      ) : items.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-sm text-slate-600" data-testid="documents-empty">
          <FileText className="mx-auto h-8 w-8 text-slate-300 mb-3" />
          No documents uploaded for this case.
        </CardContent></Card>
      ) : (
        <div className="space-y-2" data-testid="documents-list">
          {items.map((d) => (
            <Card key={d.id} data-testid={`document-${d.id}`} className={d.status === "archived" ? "opacity-60" : ""}>
              <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="font-medium text-slate-900 text-sm truncate">{d.original_filename}</p>
                    <Badge variant="secondary" className="text-xs">{CATEGORY_LABELS[d.category] || d.category}</Badge>
                    {d.status === "archived" && <Badge className="bg-slate-100 text-slate-500 border-transparent">Archived</Badge>}
                  </div>
                  <p className="text-xs text-slate-500 mt-1">
                    {d.content_type} · {(d.size / 1024).toFixed(0)} KB · uploaded {fmt(d.created_at)}
                    {d.description ? ` · ${d.description}` : ""}
                  </p>
                </div>
                <div className="flex gap-2">
                  <a href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${d.id}/download`} target="_blank" rel="noreferrer"
                    data-testid={`doc-download-${d.id}`}
                    className="inline-flex h-9 items-center rounded-md border border-border px-3 text-xs font-medium text-primary hover:bg-muted">
                    Download
                  </a>
                  {canUpload && d.status !== "archived" && (
                    <Button variant="outline" className="h-9 text-xs" data-testid={`doc-archive-${d.id}`} onClick={() => archive(d.id)}>
                      <Archive className="h-3.5 w-3.5 mr-1" /> Archive
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
